import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, activeIndex, onClose, onNavigate }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Tastaturnavigation (Escape, Venstre, Højre piletaster)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    // Forhindr scroll på baggrunden, når lightbox er åben
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [activeIndex]);

  const handlePrev = () => {
    const newIndex = activeIndex === 0 ? images.length - 1 : activeIndex - 1;
    onNavigate(newIndex);
  };

  const handleNext = () => {
    const newIndex = activeIndex === images.length - 1 ? 0 : activeIndex + 1;
    onNavigate(newIndex);
  };

  // Touch-swipe understøttelse på mobil
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  if (activeIndex === null || activeIndex === undefined) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950/95 p-4 md:p-8 animate-in fade-in duration-200"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-slate-900/80 text-white hover:text-blue-500 hover:bg-slate-800 transition-all active:scale-95"
        aria-label="Luk galleri"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Navigation Buttons (Desktop) */}
      <button
        onClick={handlePrev}
        className="absolute left-4 z-40 hidden md:flex h-14 w-14 items-center justify-center rounded-full bg-slate-900/60 text-white hover:text-blue-500 hover:bg-slate-800 transition-all active:scale-95"
        aria-label="Forrige billede"
      >
        <ChevronLeft className="h-8 w-8" />
      </button>
      <button
        onClick={handleNext}
        className="absolute right-4 z-40 hidden md:flex h-14 w-14 items-center justify-center rounded-full bg-slate-900/60 text-white hover:text-blue-500 hover:bg-slate-800 transition-all active:scale-95"
        aria-label="Næste billede"
      >
        <ChevronRight className="h-8 w-8" />
      </button>

      {/* Image Container */}
      <div className="relative flex max-h-[80vh] max-w-full items-center justify-center select-none">
        <img
          src={images[activeIndex]}
          alt={`Galleri billede ${activeIndex + 1}`}
          className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl animate-in zoom-in-95 duration-200"
          draggable={false}
        />
      </div>

      {/* Info & Mobile Navigation */}
      <div className="mt-6 flex flex-col items-center gap-4 text-center">
        <p className="text-sm font-medium text-slate-400">
          Billede <span className="text-white font-semibold">{activeIndex + 1}</span> af{" "}
          <span className="text-white font-semibold">{images.length}</span>
        </p>

        {/* Mobile Navigation Buttons */}
        <div className="flex md:hidden items-center gap-4">
          <button
            onClick={handlePrev}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900/80 text-white hover:text-blue-500 transition-all"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={handleNext}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900/80 text-white hover:text-blue-500 transition-all"
            aria-label="Næste billede"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
